/** @format */

import { useEffect, useState } from "react";
import axios from "./axios";
import { GLOBAL_DISCOUNT } from "../screens/customize/data";

// The two sale tiers, from the same places checkout reads them:
//
//   global  GLOBAL_DISCOUNT in screens/customize/data.js (code)
//   fabric  _fabric.ocs_discount in the database, set by the owner with an
//           update query
//
// Shown prices used to hard-code both as `price * 0.5 * 0.6`. Changing the DB
// value alone then left every "From" price and the structured data out of step
// with what customers actually paid.
//
// OCS gives every fabric the same ocs_discount, so one value is read and used
// for every shade. If fabrics ever get different discounts, this has to read
// them per shade instead.

// Used until the request answers, and if it fails, so a price is never blank.
const FALLBACK_FABRIC_DISCOUNT = 0.4;

let fabricDiscount = null;
let pending = null;

const loadFabricDiscount = () => {
	if (!pending) {
		pending = axios
			.post("editBlind/filterFabrics", {
				width: 24,
				shade: "roller shade",
				group: "All",
				series: "All",
			})
			.then((res) => {
				const value = parseFloat(
					res.data?.seriesColourImage?.[0]?.ocs_discount?.[0]
				);
				fabricDiscount = Number.isFinite(value)
					? value
					: FALLBACK_FABRIC_DISCOUNT;
				return fabricDiscount;
			})
			.catch(() => {
				pending = null; // try again on the next page
				return FALLBACK_FABRIC_DISCOUNT;
			});
	}
	return pending;
};

export const useSaleDiscounts = () => {
	const [fabric, setFabric] = useState(
		fabricDiscount ?? FALLBACK_FABRIC_DISCOUNT
	);
	useEffect(() => {
		let cancelled = false;
		loadFabricDiscount().then((value) => {
			if (!cancelled) setFabric(value);
		});
		return () => {
			cancelled = true;
		};
	}, []);
	return { global: GLOBAL_DISCOUNT, fabric };
};

// What a customer pays for something listed at `price` before the sale.
export const applySale = (price, { global, fabric }) =>
	price * (1 - global) * (1 - fabric);

// "50% + 40%", or "50%" when there is no second tier.
export const saleLabel = ({ global, fabric }) =>
	[global, fabric]
		.filter((d) => d > 0)
		.map((d) => `${Math.round(d * 100)}%`)
		.join(" + ");
